import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, CloudOff, RefreshCw, CheckCircle } from 'lucide-react';
import { getPendingCount, syncPendingOrders, syncPendingPayments, setupConnectivityListeners } from '../utils/offlineSync';
import posApi from '../utils/posApi';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState({ orders: 0, payments: 0, total: 0 });
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    // Update pending count
    updatePendingCount();

    // Setup connectivity listeners
    const initialOnline = setupConnectivityListeners(
      handleOnline,
      handleOffline
    );
    
    setIsOnline(initialOnline);

    // Check pending count every 30 seconds
    const interval = setInterval(updatePendingCount, 30000);

    // Listen for service worker messages
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      clearInterval(interval);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, []);

  const updatePendingCount = async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  };

  const handleOnline = async () => {
    setIsOnline(true);
    await updatePendingCount();
    
    // Auto-sync if there's pending data
    if (pendingCount.total > 0) {
      await handleSync();
    }
  };

  const handleOffline = () => {
    setIsOnline(false);
  };

  const handleServiceWorkerMessage = (event) => {
    if (event.data && event.data.type === 'SYNC_COMPLETE') {
      console.log('✅ Background sync completed');
      updatePendingCount();
      setLastSyncTime(new Date());
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleSync = async () => {
    if (!isOnline) {
      alert('⚠️ Cannot sync while offline. Please check your internet connection.');
      return;
    }

    setSyncing(true);
    try {
      console.log('🔄 Starting manual sync...');
      
      // Sync orders
      const ordersResult = await syncPendingOrders(posApi);
      console.log('Orders sync result:', ordersResult);
      
      // Sync payments
      const paymentsResult = await syncPendingPayments(posApi);
      console.log('Payments sync result:', paymentsResult);
      
      // Update count
      await updatePendingCount();
      setLastSyncTime(new Date());
      
      // Show success message
      const totalSynced = ordersResult.synced + paymentsResult.synced;
      if (totalSynced > 0) {
        alert(`✅ Synced ${totalSynced} items successfully!`);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        alert('ℹ️ No pending items to sync');
      }
    } catch (error) {
      console.error('❌ Sync failed:', error);
      alert('❌ Sync failed. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  // Don't show if online and no pending items
  if (isOnline && pendingCount.total === 0 && !showSuccess) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="mb-2 animate-slide-down">
          <div className="bg-red-900/95 backdrop-blur-sm border border-red-600 rounded-lg px-4 py-3 shadow-xl">
            <div className="flex items-center gap-3">
              <WifiOff className="w-5 h-5 text-red-300 animate-pulse" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-100">You're offline</p>
                <p className="text-xs text-red-200">
                  Orders will be saved and synced when connection is restored
                </p>
              </div>
              <CloudOff className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </div>
      )}

      {/* Pending Items Banner */}
      {pendingCount.total > 0 && (
        <div className="animate-slide-down">
          <div className={`backdrop-blur-sm border rounded-lg px-4 py-3 shadow-xl ${
            isOnline 
              ? 'bg-purple-900/95 border-purple-600' 
              : 'bg-yellow-900/95 border-yellow-600'
          }`}>
            <div className="flex items-center gap-3">
              <RefreshCw className={`w-5 h-5 ${
                syncing ? 'animate-spin' : ''
              } ${isOnline ? 'text-purple-300' : 'text-yellow-300'}`} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${
                  isOnline ? 'text-purple-100' : 'text-yellow-100'
                }`}>
                  {pendingCount.total} item(s) pending sync
                </p>
                <p className={`text-xs ${
                  isOnline ? 'text-purple-200' : 'text-yellow-200'
                }`}>
                  {pendingCount.orders} orders, {pendingCount.payments} payments
                </p>
              </div>
              {isOnline && (
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="px-3 py-1 bg-white text-purple-900 rounded text-xs font-semibold hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
              )}
            </div>
            {lastSyncTime && (
              <p className={`text-xs mt-1 ${
                isOnline ? 'text-purple-300' : 'text-yellow-300'
              }`}>
                Last synced: {lastSyncTime.toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Success Banner */}
      {showSuccess && (
        <div className="mt-2 animate-slide-down">
          <div className="bg-green-900/95 backdrop-blur-sm border border-green-600 rounded-lg px-4 py-3 shadow-xl">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-300" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-100">Sync complete!</p>
                <p className="text-xs text-green-200">
                  All data has been synchronized
                </p>
              </div>
              <Wifi className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineIndicator;
